/**
 * CreateSetupModal.tsx
 * Uses ValidateContext – no props.
 * Title: SETUP
 * Fields: Name | Ground Station Provider (row 1)
 *         Model Type | Model Mode        (row 2)
 *         Description                    (full width)
 *
 * Model Type options come from getSetupConfig API (via context.modelTypeOptions).
 * Model Mode is a fixed 2-option dropdown: Open | Close.
 *
 * API integration:
 *   – modelType and modelMode are included in createSetup payload.
 *   – When setup-config API is ready, swap the stub in validate.api.ts; no change needed here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './CreateSetupModal.scss';
import CustomSelect from '../../CommonComponents/CustomSelect';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import ConfirmationDialog from '../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import { CrossSvg, InfoSvg } from '../../CommonComponents/svgs/Svgs';
import { showToastMessage } from '../../../utils/commonFunctions';
import { createSetup, extractApiError, getAvailableSubsystems } from '../api/validate.api';
import type { GroundStationProvider, SelectOption, ValidateSetupType } from '../types/validateDetails.types';
import { useValidateContext } from '../context/ValidateContext';
import {
  SETUP_NAME_MAX_LEN,
  SETUP_DESCRIPTION_MAX_LEN,
  CHAR_LIMIT_TOAST_COOLDOWN_MS,
} from '../constants/validateDetails.constants';
import { getSubsystemIcon } from '../../Missions/Satellites/TestCase/svgs/TestCaseSvgs';

export type { GroundStationProvider };

// ─── Validation Patterns & Messages ───────────────────────────────────────────
const SETUP_NAME_ALLOWED_PATTERN = /^[a-zA-Z0-9\-_ ]*$/;
const SPECIAL_CHAR_ERROR_MSG = "Special characters not allowed. Only letters, numbers, '-', '_' and spaces are allowed";

// ─── Model Mode — fixed options (no API needed) ───────────────────────────────
type ModelModeOption = SelectOption<string>;

const MODEL_MODE_OPTIONS: ModelModeOption[] = [
  { label: 'Open', value: 'open' },
  { label: 'Close', value: 'close' },
];

const OPEN_MODEL_MODE = MODEL_MODE_OPTIONS[0];

const SETUP_TYPE_OPTIONS: Array<SelectOption<ValidateSetupType>> = [
  { label: 'Flatsat', value: 'FLATSAT' },
  { label: 'TrueTwin', value: 'TRUETWIN' },
  { label: 'Subsystem Unit Testing', value: 'SUBSYSTEM_UNIT_TESTING' },
];

// ─── Form ─────────────────────────────────────────────────────────────────────

type FormValues = {
  setupType: ValidateSetupType;
  name: string;
  groundStationProvider: GroundStationProvider | null;
  description: string;
  /** Selected model type. Value comes from modelTypeOptions (setup-config API). */
  modelType: { label: string; value: string } | null;
  /** Selected model mode. Fixed: Open | Close. */
  modelMode: ModelModeOption | null;
  /** Connected subsystems selected for this setup. */
  connectedSubsystems: string[];
};

type FormErrors = Partial<Record<keyof FormValues, string>>;

const ERROR_BORDER = '0.5px solid rgba(255, 100, 80, 0.6)';

const SELECT_BASE_PROPS = {
  controllHeight: '34px',
  padding: '0px 4px',
  background: 'rgba(10, 10, 10, 0.65)',
  color: '#f0f0f0',
  fullWidth: true,
} as const;

function fireCharLimitToast(
  toastRef: React.MutableRefObject<number>,
  fieldLabel: string,
  maxLen: number,
): void {
  const now = Date.now();
  if (now - toastRef.current > CHAR_LIMIT_TOAST_COOLDOWN_MS) {
    toastRef.current = now;
    showToastMessage('info', `${fieldLabel} cannot exceed ${maxLen} characters`);
  }
}

const EMPTY_FORM: FormValues = {
  setupType: 'FLATSAT',
  name: '',
  groundStationProvider: null,
  description: '',
  modelType: null,
  modelMode: null,
  connectedSubsystems: [],
};

function validateForm(values: FormValues): FormErrors {
  const errors: FormErrors = {};
  const isTrueTwin = values.setupType === 'TRUETWIN';

  if (!values.name.trim()) {
    errors.name = 'Name is required';
  } else if (!SETUP_NAME_ALLOWED_PATTERN.test(values.name)) {
    errors.name = SPECIAL_CHAR_ERROR_MSG;
  }
  if (!isTrueTwin && !values.groundStationProvider) {
    errors.groundStationProvider = 'Please select a provider';
  }
  if (!isTrueTwin && !values.modelType) errors.modelType = 'Please select a model type';
  if (values.setupType === 'FLATSAT' && !values.modelMode) {
    errors.modelMode = 'Please select a model mode';
  }
  if (!isTrueTwin && values.connectedSubsystems.length === 0) {
    errors.connectedSubsystems = 'Please select at least one subsystem';
  }
  if (values.description.length > SETUP_DESCRIPTION_MAX_LEN) {
    errors.description = `Description cannot exceed ${SETUP_DESCRIPTION_MAX_LEN} characters`;
  }
  return errors;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateSetupModal = () => {
  const {
    mission_id,
    selectedSatellite,
    groundStationOptions,
    loadingProviders,
    modelTypeOptions,
    loadingSetupConfig,
    isCreateModalOpen: open,
    closeCreateModal: onClose,
    setSetups,
    onSetupSelect,
  } = useValidateContext();

  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [subsystemOptions, setSubsystemOptions] = useState<SelectOption<string>[]>([]);
  const [loadingSubsystems, setLoadingSubsystems] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Two independent throttle refs — one per char-limit field
  const lastNameToastAtRef = useRef(0);
  const lastDescriptionToastAtRef = useRef(0);

  const satelliteGroupId = selectedSatellite?.value?.groupId ?? '';
  const satelliteId = selectedSatellite?.value?.satId ?? '';
  const canSave = Boolean(satelliteGroupId && satelliteId && mission_id);
  const isSubsystemUnitTesting = values.setupType === 'SUBSYSTEM_UNIT_TESTING';
  const displayedModelMode = isSubsystemUnitTesting ? OPEN_MODEL_MODE : values.modelMode;
  const subsystemSelectionHelp = isSubsystemUnitTesting
    ? 'Select one subsystem for this unit-test setup.'
    : 'Select all subsystems connected to this Flatsat setup.';

  const setField = useCallback(
    <K extends keyof FormValues>(key: K, val: FormValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: val }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
      setApiError(null);
    },
    [],
  );

  const isFormDirty = useMemo(() => {
    return (
      values.name.length > 0 ||
      values.description.length > 0 ||
      values.connectedSubsystems.length > 0 ||
      values.setupType !== 'FLATSAT' ||
      (groundStationOptions.length > 1 && values.groundStationProvider !== null) ||
      (modelTypeOptions.length > 1 && values.modelType !== null) ||
      values.modelMode !== null
    );
  }, [values, groundStationOptions.length, modelTypeOptions.length]);

  const handleClose = useCallback(() => {
    setValues(EMPTY_FORM);
    setErrors({});
    setApiError(null);
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (isFormDirty) {
      setShowDiscardConfirm(true);
    } else {
      handleClose();
    }
  }, [isFormDirty, handleClose]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    handleClose();
  }, [handleClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setValues(EMPTY_FORM);
      setErrors({});
      setApiError(null);
      setShowDiscardConfirm(false);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDiscardConfirm) {
          setShowDiscardConfirm(false);
        } else {
          requestClose();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, showDiscardConfirm, requestClose]);

  useEffect(() => {
    if (!open || !mission_id) return;
    let cancelled = false;

    setLoadingSubsystems(true);
    getAvailableSubsystems(mission_id)
      .then(({ data, apiError }) => {
        if (cancelled) return;
        if (apiError) showToastMessage('error', apiError);
        setSubsystemOptions(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubsystems(false);
      });

    return () => { cancelled = true; };
  }, [open, mission_id]);

  // Auto-select when only one ground-station provider is available
  useEffect(() => {
    if (!open) return;
    if (!values.groundStationProvider && groundStationOptions.length === 1) {
      setField('groundStationProvider', groundStationOptions[0]);
    }
  }, [open, groundStationOptions, values.groundStationProvider, setField]);

  // Auto-select when only one model type is available
  useEffect(() => {
    if (!open) return;
    if (!values.modelType && modelTypeOptions.length === 1) {
      setField('modelType', modelTypeOptions[0]);
    }
  }, [open, modelTypeOptions, values.modelType, setField]);

  const standardModelTypeOptions = useMemo(
    () => modelTypeOptions.filter((opt) => opt.value.toUpperCase() !== 'TRUETWIN' && opt.label.toUpperCase() !== 'TRUETWIN'),
    [modelTypeOptions],
  );

  const setSetupType = useCallback((setupType: ValidateSetupType) => {
    setValues((prev) => {
      const isPrevTrueTwin = prev.modelType?.value.toUpperCase() === 'TRUETWIN';
      const nextModelType = setupType === 'TRUETWIN'
        ? { label: 'TrueTwin', value: 'TrueTwin' }
        : (isPrevTrueTwin ? null : prev.modelType);

      const isSameTab = prev.setupType === setupType;

      return {
        ...prev,
        setupType,
        modelType: nextModelType,
        modelMode: setupType === 'SUBSYSTEM_UNIT_TESTING' ? OPEN_MODEL_MODE : prev.modelMode,
        connectedSubsystems: isSameTab ? prev.connectedSubsystems : [],
      };
    });
    setErrors((prev) => ({ ...prev, setupType: undefined, modelType: undefined, modelMode: undefined, connectedSubsystems: undefined }));
    setApiError(null);
  }, []);

  const onNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (!SETUP_NAME_ALLOWED_PATTERN.test(next)) {
      showToastMessage('info', SPECIAL_CHAR_ERROR_MSG);
      return;
    }
    setField('name', next.length <= SETUP_NAME_MAX_LEN ? next : next.slice(0, SETUP_NAME_MAX_LEN));
    if (next.length >= SETUP_NAME_MAX_LEN) {
      fireCharLimitToast(lastNameToastAtRef, 'Setup name', SETUP_NAME_MAX_LEN);
    }
  }, [setField]);

  const onDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setField(
      'description',
      next.length <= SETUP_DESCRIPTION_MAX_LEN ? next : next.slice(0, SETUP_DESCRIPTION_MAX_LEN),
    );
    if (next.length >= SETUP_DESCRIPTION_MAX_LEN) {
      fireCharLimitToast(lastDescriptionToastAtRef, 'Description', SETUP_DESCRIPTION_MAX_LEN);
    }
  }, [setField]);

  const toggleSubsystem = useCallback((subsystem: string) => {
    setValues((prev) => {
      const isSelected = prev.connectedSubsystems.includes(subsystem);
      if (prev.setupType === 'SUBSYSTEM_UNIT_TESTING') {
        return {
          ...prev,
          connectedSubsystems: isSelected ? [] : [subsystem],
        };
      }
      return {
        ...prev,
        connectedSubsystems: isSelected
          ? prev.connectedSubsystems.filter((item) => item !== subsystem)
          : [...prev.connectedSubsystems, subsystem],
      };
    });
    setErrors((prev) => ({ ...prev, connectedSubsystems: undefined }));
    setApiError(null);
  }, []);

  const isAllSubsystemsSelected = useMemo(
    () => subsystemOptions.length > 0 && subsystemOptions.every((opt) => values.connectedSubsystems.includes(opt.value)),
    [subsystemOptions, values.connectedSubsystems],
  );

  const toggleSelectAllSubsystems = useCallback(() => {
    setValues((prev) => ({
      ...prev,
      connectedSubsystems: isAllSubsystemsSelected
        ? []
        : subsystemOptions.map((opt) => opt.value),
    }));
    setErrors((prev) => ({ ...prev, connectedSubsystems: undefined }));
    setApiError(null);
  }, [isAllSubsystemsSelected, subsystemOptions]);

  const onOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) requestClose();
  }, [requestClose]);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    const errs = validateForm(values);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    setApiError(null);

    try {
      const isTrueTwin = values.setupType === 'TRUETWIN';
      const { setup: newSetup, message } = await createSetup(mission_id, satelliteGroupId, satelliteId, {
        name: values.name.trim(),
        groundStationProviderId: isTrueTwin ? undefined : values.groundStationProvider?.value,
        description: values.description.trim(),
        modelType: isTrueTwin ? undefined : values.modelType?.value,
        modelMode: isTrueTwin ? undefined : (isSubsystemUnitTesting ? OPEN_MODEL_MODE.value : values.modelMode?.value),
        connectedSubsystems: isTrueTwin ? (values.connectedSubsystems.length ? values.connectedSubsystems : undefined) : values.connectedSubsystems,
        setupType: values.setupType,
      });
      setSetups((prev) => [...prev, newSetup]);
      // Ensure the newly created setup is selected (instead of defaulting to first card).
      if (newSetup?.setupId) onSetupSelect(newSetup.setupId);
      showToastMessage('success', message || 'Setup created successfully');
      handleClose();
    } catch (err: unknown) {
      const message = extractApiError(err, 'Something went wrong. Please try again.');
      setApiError(message);
      showToastMessage('error', message);
    } finally {
      setSaving(false);
    }
  }, [canSave, mission_id, values, isSubsystemUnitTesting, satelliteGroupId, satelliteId, setSetups, handleClose, onSetupSelect]);

  if (!open) return null;

  const isLoading = loadingProviders || loadingSetupConfig || loadingSubsystems;

  return (
    <>
      <div
        className="csm__overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="csm-title"
        onClick={onOverlayClick}
      >
        <div className="csm__panel" data-testid="create-setup-modal">
          {/* ── Header ── */}
          <div className="csm__header">
            <div className="csm__header__row">
              <h2 className="csm__header__title" id="csm-title">MODEL SETUP</h2>
              <button
                className="csm__header__close"
                type="button"
                aria-label="Close"
                onClick={requestClose}
                data-testid="create-setup-close-btn"
              >
                <CrossSvg />
              </button>
            </div>
            <div className="csm__header__divider" />
          </div>

        {/* ── Body ── */}
        <div className="csm__body">

          {/* Setup Type Tabs */}
          <div className="csm__field csm__field--full">
            <div className="csm__setup-type" role="group" aria-label="Setup type">
              {SETUP_TYPE_OPTIONS.map((option) => {
                const isActive = values.setupType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={isActive}
                    className={`csm__setup-type__option${isActive ? ' csm__setup-type__option--active' : ''}`}
                    onClick={() => setSetupType(option.value)}
                    data-testid={`create-setup-type-${option.value.toLowerCase()}`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* TrueTwin vs Non-TrueTwin Layout */}
          {values.setupType === 'TRUETWIN' ? (
            /* TrueTwin Layout: Row 1 has Name | Model Type side-by-side */
            <div className="csm__row">
              <div className="csm__field">
                <label className="csm__field__label" htmlFor="csm-name">Name*</label>
                <input
                  id="csm-name"
                  className={`csm__field__input${errors.name ? ' csm__field__input--error' : ''}`}
                  type="text"
                  placeholder="Name"
                  value={values.name}
                  onChange={onNameChange}
                  maxLength={SETUP_NAME_MAX_LEN}
                  autoComplete="off"
                  data-testid="create-setup-name-input"
                />
                {errors.name && <span className="csm__error">{errors.name}</span>}
              </div>

              <div className="csm__field">
                <span className="csm__field__label">Model Type*</span>
                <div className={`csm__select${errors.modelType ? ' csm__select--error' : ''}`}>
                  <CustomSelect
                    {...SELECT_BASE_PROPS}
                    options={modelTypeOptions}
                    placeholder="TrueTwin"
                    value={values.modelType || { label: 'TrueTwin', value: 'TrueTwin' }}
                    onChange={(opt) => setField('modelType', opt as { label: string; value: string } | null)}
                    isSearchable={false}
                    isDisabled={true}
                    border={errors.modelType ? ERROR_BORDER : undefined}
                  />
                </div>
                {errors.modelType && <span className="csm__error">{errors.modelType}</span>}
              </div>
            </div>
          ) : (
            /* Non-TrueTwin Layout (Flatsat / Subsystem Unit Testing) */
            <>
              <div className="csm__row">
                <div className="csm__field">
                  <label className="csm__field__label" htmlFor="csm-name">Name*</label>
                  <input
                    id="csm-name"
                    className={`csm__field__input${errors.name ? ' csm__field__input--error' : ''}`}
                    type="text"
                    placeholder="Name"
                    value={values.name}
                    onChange={onNameChange}
                    maxLength={SETUP_NAME_MAX_LEN}
                    autoComplete="off"
                    data-testid="create-setup-name-input"
                  />
                  {errors.name && <span className="csm__error">{errors.name}</span>}
                </div>

                <div className="csm__field">
                  <span className="csm__field__label">Ground Station Provider*</span>
                  <div className={`csm__select${errors.groundStationProvider ? ' csm__select--error' : ''}`}>
                    <CustomSelect
                      {...SELECT_BASE_PROPS}
                      options={groundStationOptions}
                      placeholder={loadingProviders ? 'Loading…' : 'Select'}
                      value={values.groundStationProvider}
                      onChange={(opt) => setField('groundStationProvider', opt as GroundStationProvider | null)}
                      isSearchable={true}
                      isDisabled={loadingProviders}
                      border={errors.groundStationProvider ? ERROR_BORDER : undefined}
                    />
                  </div>
                  {errors.groundStationProvider && (
                    <span className="csm__error">{errors.groundStationProvider}</span>
                  )}
                </div>
              </div>

              <div className="csm__row">
                <div className="csm__field">
                  <span className="csm__field__label">Model Type*</span>
                  <div className={`csm__select${errors.modelType ? ' csm__select--error' : ''}`}>
                    <CustomSelect
                      {...SELECT_BASE_PROPS}
                      options={standardModelTypeOptions}
                      placeholder={loadingSetupConfig ? 'Loading…' : 'Select'}
                      value={values.modelType}
                      onChange={(opt) => setField('modelType', opt as { label: string; value: string } | null)}
                      isSearchable={true}
                      isDisabled={loadingSetupConfig}
                      border={errors.modelType ? ERROR_BORDER : undefined}
                    />
                  </div>
                  {errors.modelType && <span className="csm__error">{errors.modelType}</span>}
                </div>

                <div className="csm__field">
                  <span className="csm__field__label">Model Mode*</span>
                  <div className={`csm__select${errors.modelMode ? ' csm__select--error' : ''}`}>
                    <CustomSelect
                      {...SELECT_BASE_PROPS}
                      options={MODEL_MODE_OPTIONS}
                      placeholder="Select"
                      value={displayedModelMode}
                      onChange={(opt) => setField('modelMode', opt as ModelModeOption | null)}
                      isSearchable={true}
                      isDisabled={isSubsystemUnitTesting}
                      border={errors.modelMode ? ERROR_BORDER : undefined}
                    />
                  </div>
                  {errors.modelMode && <span className="csm__error">{errors.modelMode}</span>}
                </div>
              </div>
            </>
          )}

          {/* Description — full width */}
          <div className="csm__field csm__field--full">
            <label className="csm__field__label" htmlFor="csm-description">Description</label>
            <textarea
              id="csm-description"
              className={`csm__field__textarea${errors.description ? ' csm__field__input--error' : ''}`}
              placeholder="Description"
              value={values.description}
              onChange={onDescriptionChange}
              maxLength={SETUP_DESCRIPTION_MAX_LEN}
              data-testid="create-setup-description-input"
            />
            {errors.description && <span className="csm__error">{errors.description}</span>}
          </div>

          {/* Connected subsystems — full width (hidden for TRUETWIN) */}
          {values.setupType !== 'TRUETWIN' && (
            <div className="csm__field csm__field--full">
              <span className="csm__field__label csm__field__label--with-help">
                <span className="csm__field__label-title">
                  <span>Subsystems*</span>
                  <CustomToolTip title={subsystemSelectionHelp} placement="right" maxWidth="320px" wordBreak="normal">
                    <span className="csm__field__help-icon" aria-label="Subsystem selection guidance">
                      <InfoSvg />
                    </span>
                  </CustomToolTip>
                </span>
                {values.setupType === 'FLATSAT' && subsystemOptions.length > 0 && (
                  <button
                    type="button"
                    className="csm__subsystems__select-all-btn"
                    onClick={toggleSelectAllSubsystems}
                  >
                    {isAllSubsystemsSelected ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </span>
              <div className={`csm__subsystems${errors.connectedSubsystems ? ' csm__subsystems--error' : ''}`}>
                {loadingSubsystems && (
                  <span className="csm__subsystems__message">Loading subsystems...</span>
                )}
                {!loadingSubsystems && subsystemOptions.length === 0 && (
                  <span className="csm__subsystems__message">No subsystems available</span>
                )}
                {!loadingSubsystems && subsystemOptions.map((option) => {
                  const isSelected = values.connectedSubsystems.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`csm__subsystems__chip${isSelected ? ' csm__subsystems__chip--selected' : ''}`}
                      aria-pressed={isSelected}
                      onClick={() => toggleSubsystem(option.value)}
                    >
                      <span className="csm__subsystems__icon">{getSubsystemIcon(option.label)}</span>
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
              {errors.connectedSubsystems && <span className="csm__error">{errors.connectedSubsystems}</span>}
            </div>
          )}

          {apiError && <span className="csm__error">{apiError}</span>}
        </div>

        {/* ── Footer ── */}
        <div className="csm__footer">
          <div className="csm__footer__divider" />
          <div className="csm__footer__actions">
            <button
              type="button"
              className="csm__btn csm__btn--close"
              onClick={requestClose}
              disabled={saving}
              data-testid="create-setup-cancel-btn"
            >
              Close
            </button>
            <button
              type="button"
              className={`csm__btn csm__btn--save${saving ? ' csm__btn--saving' : ''}`}
              onClick={handleSave}
              disabled={saving || !canSave || isLoading}
              title={!canSave ? 'Select a satellite first' : undefined}
              data-testid="create-setup-submit-btn"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ConfirmationDialog
      type="discard"
      open={showDiscardConfirm}
      title="Discard Changes"
      description="Are you sure you want to discard? All unsaved changes will be lost."
      confirm_button_text="Discard"
      cancel_button_text="Cancel"
      onConfirm={handleConfirmDiscard}
      onCancel={() => setShowDiscardConfirm(false)}
      dataTestIdPrefix="create-setup-discard"
    />
  </>
);
};

export default CreateSetupModal;
